/**
 * Mock posting analytics for Radar → Performance → Owned.
 *
 * Shape mirrors Zernio GET /v1/analytics for *connected* brand accounts
 * (`source=late` vs `source=external`). Competitor/listen-target feeds are
 * not in this payload — see TrackedListeningInsightsPage.
 */
import type {
  PostingInsightDaily,
  PostingInsightFollowers,
  PostingInsightMetrics,
  PostingInsightPost,
  PostingInsightSource,
  SocialPlatform,
} from '@/client/types/marketing';

/** Coverage archive picker. Performance public tabs use `PUBLIC_RANGE_PRESETS`. */
export const INSIGHT_RANGE_DAYS = [7, 30, 90] as const;
export type InsightRangeDays = (typeof INSIGHT_RANGE_DAYS)[number];
export const PUBLIC_MAX_RANGE_DAYS = 90;
/** Sentiment / Tracked / Trends / KPIs public rail — server clamps 1–90 (#1227). */
export const PUBLIC_RANGE_PRESETS = [7, 14, 30, 60, 90] as const;

/** Owned / KPIs — store holds up to a year once backfilled. */
export const OWNED_RANGE_PRESETS = [7, 30, 90, 365] as const;
export type OwnedRangePreset = (typeof OWNED_RANGE_PRESETS)[number];
export const OWNED_MAX_RANGE_DAYS = 365;

const DAY_MS_RANGE = 86_400_000;

/** Inclusive day count from a start date through today, clamped to 1…365. UTC day keys. */
export function ownedDaysFromStart(fromYmd: string, end = new Date()): number {
  const from = Date.parse(`${fromYmd}T00:00:00Z`);
  if (Number.isNaN(from)) return 30;
  const endMs = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
  const days = Math.round((endMs - from) / DAY_MS_RANGE) + 1;
  return Math.max(1, Math.min(OWNED_MAX_RANGE_DAYS, days));
}

export function ownedStartDateForDays(days: number, end = new Date()): string {
  const d = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
  d.setUTCDate(d.getUTCDate() - Math.max(1, Math.min(OWNED_MAX_RANGE_DAYS, days)));
  return d.toISOString().slice(0, 10);
}

export function utcTodayYmd(end = new Date()): string {
  return new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()))
    .toISOString()
    .slice(0, 10);
}

/** Start of an inclusive last-N-days window ending today (30d → today and the 29 days before). */
export function ownedInclusiveStart(days: number, end = new Date()): string {
  const n = Math.max(1, Math.min(OWNED_MAX_RANGE_DAYS, days));
  const d = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
  d.setUTCDate(d.getUTCDate() - (n - 1));
  return d.toISOString().slice(0, 10);
}

/** Absolute window to send with Owned requests so `series` comes back dense. */
export function ownedWindowBounds(
  range: number,
  customFrom: string | null = null,
  end = new Date(),
): { from: string; to: string; range_days: number } {
  const to = utcTodayYmd(end);
  const from = customFrom ?? ownedInclusiveStart(range, end);
  const fromMs = Date.parse(`${from}T00:00:00Z`);
  const toMs = Date.parse(`${to}T00:00:00Z`);
  const inclusive =
    Number.isFinite(fromMs) && Number.isFinite(toMs)
      ? Math.round((toMs - fromMs) / DAY_MS_RANGE) + 1
      : range;
  return {
    from,
    to,
    range_days: Math.max(1, Math.min(OWNED_MAX_RANGE_DAYS, inclusive)),
  };
}

const DAY_MS = 86_400_000;
/** Inclusive window ending 2026-08-18 — matches the screenshot's "last 30 days". */
const ANCHOR = Date.parse('2026-08-18T12:00:00Z');

const HANDLES: Partial<Record<SocialPlatform, string>> = {
  instagram: '@fairful',
  facebook: 'Fairful',
  x: '@fairful',
  linkedin: 'fairful',
};

const FOLLOWER_BASE: Partial<Record<SocialPlatform, number>> = {
  instagram: 128_400,
  facebook: 42_100,
  x: 54_000,
  linkedin: 19_200,
};

const TONES = [
  'bg-gradient-to-br from-amber-300 via-rose-400 to-fuchsia-500',
  'bg-gradient-to-br from-sky-400 to-blue-700',
  'bg-gradient-to-br from-emerald-400 to-teal-700',
  'bg-gradient-to-br from-violet-500 to-indigo-800',
  'bg-gradient-to-br from-orange-400 to-rose-600',
];

const CAPTIONS = [
  'Summer stays are live ☀️ Labadi · Cape Coast · Kumasi.',
  'Hosts deserve certainty. Our guarantee covers verified bookings.',
  'This month’s newsletter is out — new stays and host stories.',
  'POV: your booking is guaranteed 👀 #FairfulSummer',
  'Three cities. One host guarantee. Book with confidence.',
  'Found this gem from a Cape Coast host — save for your next trip.',
  'What the host guarantee actually covers (and what it doesn’t).',
  'Weekend in Labadi: pool, grill, 12-minute walk to the beach.',
  'Payouts landed. Hosts, check your dashboard.',
  'West Africa summer, mapped. Where are you staying?',
];

function isoDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

function emptyMetrics(): PostingInsightMetrics {
  return {
    likes: 0,
    comments: 0,
    shares: 0,
    saves: 0,
    clicks: 0,
    views: 0,
    follows: 0,
    impressions: 0,
    reach: 0,
  };
}

function addMetrics(a: PostingInsightMetrics, b: PostingInsightMetrics, w = 1): PostingInsightMetrics {
  return {
    likes: a.likes + b.likes * w,
    comments: a.comments + b.comments * w,
    shares: a.shares + b.shares * w,
    saves: a.saves + b.saves * w,
    clicks: a.clicks + b.clicks * w,
    views: a.views + b.views * w,
    follows: a.follows + b.follows * w,
    impressions: a.impressions + b.impressions * w,
    reach: a.reach + b.reach * w,
  };
}

function roundMetrics(m: PostingInsightMetrics): PostingInsightMetrics {
  return {
    likes: Math.round(m.likes),
    comments: Math.round(m.comments),
    shares: Math.round(m.shares),
    saves: Math.round(m.saves),
    clicks: Math.round(m.clicks),
    views: Math.round(m.views),
    follows: Math.round(m.follows),
    impressions: Math.round(m.impressions),
    reach: Math.round(m.reach),
  };
}

/**
 * Zernio's per-row ER: (likes + comments + shares + saves) / impressions, or
 * views where a platform reports no impressions (TikTok, YouTube). Clicks are
 * not engagement there, and reach is never the denominator. Checked against its
 * Platform Breakdown: Facebook 22/978 = 2.25%, TikTok 227/6.9K = 3.27%.
 */
function engagementRate(m: PostingInsightMetrics): number {
  const denom = m.impressions || m.views || 0;
  if (!denom) return 0;
  return (m.likes + m.comments + m.shares + m.saves) / denom;
}

/** Spread a post's lifetime metrics across days after publish (front-loaded). */
function decay(ageDays: number): number {
  if (ageDays < 0) return 0;
  if (ageDays === 0) return 0.48;
  if (ageDays <= 7) return 0.32 * Math.exp(-ageDays / 3.2);
  if (ageDays <= 30) return 0.14 * Math.exp(-(ageDays - 7) / 12);
  return 0.06 * Math.exp(-(ageDays - 30) / 20);
}

function metricsFor(i: number, platform: SocialPlatform): PostingInsightMetrics {
  const ig = platform === 'instagram';
  const fb = platform === 'facebook';
  const base = ig ? 1 : fb ? 0.22 : platform === 'x' ? 0.45 : 0.18;
  return roundMetrics({
    likes: (ig ? 9 : fb ? 1 : 4) * (3 + (i % 5)) * base * 4,
    comments: (ig ? 1 : 0) * (i % 4 === 0 ? 1 : 0) + (i % 6 === 0 ? 1 : 0),
    shares: i % 3 === 0 ? (ig ? 1 : 0) : 0,
    saves: ig && i % 4 === 0 ? 1 : 0,
    clicks: fb || platform === 'x' ? (i % 5 === 0 ? 1 : 0) : 0,
    views: (ig ? 80 : fb ? 12 : 40) * (2 + (i % 6)) * (ig ? 1 : 0.7),
    follows: i % 7 === 0 ? 1 : 0,
    impressions: (ig ? 90 : fb ? 18 : 48) * (2 + (i % 6)) * (ig ? 1 : 0.75),
    reach: (ig ? 48 : fb ? 8 : 22) * (2 + (i % 6)),
  });
}

function buildPosts(): PostingInsightPost[] {
  const platforms: SocialPlatform[] = ['instagram', 'facebook', 'instagram', 'facebook', 'x', 'linkedin'];
  const posts: PostingInsightPost[] = [];
  for (let i = 0; i < 22; i++) {
    const platform = platforms[i % platforms.length];
    const dayOffset = Math.floor(i * 1.35) + (i % 3);
    const published = ANCHOR - (29 - Math.min(29, dayOffset)) * DAY_MS + ((i * 3) % 14) * 3_600_000;
    posts.push({
      id: `ins_${i}`,
      platform,
      handle: HANDLES[platform] ?? '@fairful',
      source: i % 4 === 0 ? 'external' : 'late',
      caption: CAPTIONS[i % CAPTIONS.length],
      publishedAt: new Date(published).toISOString(),
      mediaTone: TONES[i % TONES.length],
      mediaLabel: i % 5 === 0 ? 'Reel' : i % 3 === 0 ? 'Carousel' : 'Photo',
      metrics: metricsFor(i, platform),
    });
  }
  return posts.sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
}

export const POSTING_INSIGHT_POSTS: PostingInsightPost[] = buildPosts();

function dayRange(days: number, end = ANCHOR): string[] {
  return Array.from({ length: days }, (_, i) => isoDay(end - (days - 1 - i) * DAY_MS));
}

function dailyFromPosts(posts: PostingInsightPost[], days: number): PostingInsightDaily[] {
  const dates = dayRange(days);
  const platforms = [...new Set(posts.map((p) => p.platform))];
  const rows: PostingInsightDaily[] = [];
  for (const date of dates) {
    const t = Date.parse(`${date}T12:00:00Z`);
    for (const platform of platforms) {
      const slice = posts.filter((p) => p.platform === platform);
      let m = emptyMetrics();
      let postCount = 0;
      for (const p of slice) {
        const age = Math.round((t - Date.parse(p.publishedAt)) / DAY_MS);
        if (age === 0) postCount += 1;
        const w = decay(age);
        if (w > 0) m = addMetrics(m, p.metrics, w);
      }
      const r = roundMetrics(m);
      rows.push({ date, platform, posts: postCount, ...r });
    }
  }
  return rows;
}

export const POSTING_INSIGHT_DAILY: PostingInsightDaily[] = dailyFromPosts(POSTING_INSIGHT_POSTS, 90);

function buildFollowers(): PostingInsightFollowers[] {
  const dates = dayRange(90);
  const platforms = Object.keys(FOLLOWER_BASE) as SocialPlatform[];
  const rows: PostingInsightFollowers[] = [];
  for (const date of dates) {
    const i = dates.indexOf(date);
    for (const platform of platforms) {
      const base = FOLLOWER_BASE[platform] ?? 1000;
      const bump = Math.floor(i * (platform === 'instagram' ? 18 : 6) + Math.sin(i / 4) * 12);
      rows.push({ date, platform, followers: base + bump });
    }
  }
  return rows;
}

export const POSTING_INSIGHT_FOLLOWERS: PostingInsightFollowers[] = buildFollowers();

/** Hourly, like the vendor's slots. Collapsing to 3h buckets hid which hour won. */
export const HEAT_HOURS = Array.from({ length: 24 }, (_, h) => h);
export const HEAT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export interface HeatCell {
  day: (typeof HEAT_DAYS)[number];
  /** 0–23 UTC. */
  hour: number;
  /** Null = never posted in this slot. 0 = posted and scored nothing. */
  value: number | null;
  posts?: number;
}

function buildHeatmap(posts: PostingInsightPost[]): HeatCell[] {
  const cells: HeatCell[] = [];
  for (const day of HEAT_DAYS) {
    for (const hour of HEAT_HOURS) {
      let value = 0;
      for (const p of posts) {
        const d = new Date(p.publishedAt);
        const dow = (d.getUTCDay() + 6) % 7;
        const h = d.getUTCHours();
        if (HEAT_DAYS[dow] === day && h === hour) {
          value += engagementRate(p.metrics) * 100 + p.metrics.likes / 20;
        }
      }
      // Seed a readable pattern even when a bucket is empty.
      const di = HEAT_DAYS.indexOf(day);
      const hi = HEAT_HOURS.indexOf(hour);
      const seed = (di === 1 && hour === 9) || (di === 3 && hour === 12) || (di === 0 && hour === 12) ? 18 : 0;
      cells.push({ day, hour, value: value + seed + ((di + hi) % 3 === 0 ? 4 : 1) });
    }
  }
  return cells;
}

export interface InsightFilters {
  platform: SocialPlatform | 'all';
  handle: string | 'all';
  source: PostingInsightSource | 'all';
  /** Last N days ending now (1–365 for Owned). */
  range: number;
  /** When set, range is derived from this start date through today. */
  customFrom: string | null;
}

export const EMPTY_INSIGHT_FILTERS: InsightFilters = {
  platform: 'all',
  handle: 'all',
  source: 'all',
  range: 30,
  customFrom: null,
};

function inRange(iso: string, range: number, end = ANCHOR): boolean {
  const t = Date.parse(iso);
  return t >= end - (range - 1) * DAY_MS && t <= end + DAY_MS;
}

export function filterPosts(filters: InsightFilters, posts = POSTING_INSIGHT_POSTS): PostingInsightPost[] {
  return posts.filter((p) => {
    if (!inRange(p.publishedAt, filters.range)) return false;
    if (filters.platform !== 'all' && p.platform !== filters.platform) return false;
    if (filters.handle !== 'all' && p.handle !== filters.handle) return false;
    if (filters.source !== 'all' && p.source !== filters.source) return false;
    return true;
  });
}

export function filterDaily(filters: InsightFilters, rows = POSTING_INSIGHT_DAILY): PostingInsightDaily[] {
  return rows.filter((r) => {
    if (!inRange(`${r.date}T12:00:00Z`, filters.range)) return false;
    if (filters.platform !== 'all' && r.platform !== filters.platform) return false;
    return true;
  });
}

export function filterFollowers(filters: InsightFilters, rows = POSTING_INSIGHT_FOLLOWERS): PostingInsightFollowers[] {
  return rows.filter((r) => {
    if (!inRange(`${r.date}T12:00:00Z`, filters.range)) return false;
    if (filters.platform !== 'all' && r.platform !== filters.platform) return false;
    return true;
  });
}

export function sumMetrics(posts: PostingInsightPost[]): PostingInsightMetrics {
  return roundMetrics(posts.reduce((acc, p) => addMetrics(acc, p.metrics), emptyMetrics()));
}

export function priorPosts(filters: InsightFilters, posts = POSTING_INSIGHT_POSTS): PostingInsightPost[] {
  const end = ANCHOR - filters.range * DAY_MS;
  return posts.filter((p) => {
    const t = Date.parse(p.publishedAt);
    if (t < end - (filters.range - 1) * DAY_MS || t > end) return false;
    if (filters.platform !== 'all' && p.platform !== filters.platform) return false;
    if (filters.handle !== 'all' && p.handle !== filters.handle) return false;
    if (filters.source !== 'all' && p.source !== filters.source) return false;
    return true;
  });
}

export function changeVsPrior(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return (current - previous) / previous;
}

export function er(m: PostingInsightMetrics): number {
  return engagementRate(m);
}

export function uniqueHandles(posts = POSTING_INSIGHT_POSTS): string[] {
  return [...new Set(posts.map((p) => p.handle))].sort();
}

export function uniquePlatforms(posts = POSTING_INSIGHT_POSTS): SocialPlatform[] {
  return [...new Set(posts.map((p) => p.platform))];
}

export function weekLabel(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  const start = new Date(d);
  start.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return start.toISOString().slice(5, 10);
}

export type TimeGrain = 'day' | 'week' | 'month';

/** Start day (YYYY-MM-DD) of the day / Monday-week / month holding `date`. Sorts across years. */
export function bucketStart(date: string, grain: TimeGrain): string {
  const iso = date.slice(0, 10);
  if (grain === 'day') return iso;
  if (grain === 'month') return `${iso.slice(0, 7)}-01`;
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

/** Axis label for a `bucketStart` key: "10-26" for day/week, "Oct 26" for month. */
export function bucketTick(start: string, grain: TimeGrain): string {
  if (grain !== 'month') return start.slice(5, 10);
  return new Date(`${start.slice(0, 10)}T12:00:00Z`).toLocaleString('en-US', {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  });
}

export function heatForPosts(posts: PostingInsightPost[]): HeatCell[] {
  return buildHeatmap(posts);
}

export function bestTimes(cells: HeatCell[], n = 3): HeatCell[] {
  return [...cells]
    .filter((c) => c.value != null)
    .sort((a, b) => (b.value as number) - (a.value as number))
    .slice(0, n);
}

export function hourLabel(hour: number): string {
  if (hour === 0) return '12am';
  if (hour < 12) return `${hour}am`;
  if (hour === 12) return '12pm';
  return `${hour - 12}pm`;
}

export interface CadencePoint {
  cadence: string;
  [platform: string]: string | number;
}

export function cadenceSeries(posts: PostingInsightPost[]): { data: CadencePoint[]; insight: string } {
  const byPlat = new Map<SocialPlatform, PostingInsightPost[]>();
  for (const p of posts) {
    const list = byPlat.get(p.platform) ?? [];
    list.push(p);
    byPlat.set(p.platform, list);
  }
  const buckets = ['1–2/wk', '3–5/wk', '6+/wk'] as const;
  const data: CadencePoint[] = buckets.map((cadence) => {
    const row: CadencePoint = { cadence };
    for (const [platform, list] of byPlat) {
      const weeks = Math.max(1, new Set(list.map((p) => weekLabel(p.publishedAt.slice(0, 10)))).size);
      const perWeek = list.length / weeks;
      const inBucket =
        cadence === '1–2/wk' ? perWeek <= 2.5 : cadence === '3–5/wk' ? perWeek <= 5.5 : perWeek > 5.5;
      const avgEr = list.length ? list.reduce((s, p) => s + engagementRate(p.metrics), 0) / list.length : 0;
      // Tilt: lighter cadence reads better in the mock, matching the Zernio pattern.
      const tilt = cadence === '1–2/wk' ? 1.15 : cadence === '3–5/wk' ? 0.92 : 0.72;
      row[platform] = Number(((inBucket ? avgEr : avgEr * 0.85) * tilt * 100).toFixed(1));
    }
    return row;
  });
  const insight = [...byPlat.entries()]
    .map(([platform, list]) => {
      const avgEr = list.reduce((s, p) => s + engagementRate(p.metrics), 0) / list.length;
      return `${platform} 1–2/wk (${(avgEr * 1.15 * 100).toFixed(1)}%)`;
    })
    .join(' · ');
  return { data, insight: `Optimal cadence per platform — ${insight}` };
}

export const ACCUMULATION = [
  { bucket: 'Publish', pct: 42 },
  { bucket: '2–7d', pct: 80 },
  { bucket: '7–30d', pct: 94 },
  { bucket: '30d+', pct: 100 },
] as const;

export function formatCompact(n?: number | null): string {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(Math.round(n));
}

export function formatEr(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

export function formatChange(change: number | null): { label: string; tone: 'up' | 'down' | 'flat' } {
  if (change == null) return { label: 'No prior', tone: 'flat' };
  if (Math.abs(change) < 0.005) return { label: 'Flat vs prior', tone: 'flat' };
  const pct = Math.round(change * 100);
  return {
    label: `${pct > 0 ? '+' : ''}${pct}% vs prior`,
    tone: pct > 0 ? 'up' : 'down',
  };
}
